import React from 'react'
import { Collapse } from 'antd'

const { Panel } = Collapse

const monthHeader = (label: string, count: number) => (
  <span className="updates-month-header">
    <span>{label}</span>
    <span className="updates-month-count">{count} update{count === 1 ? '' : 's'}</span>
  </span>
)

const getUpdates2026Panels = (panelStyle?: React.CSSProperties) => React.Children.toArray(
  <>
{/* November 2026 */}
<Panel header={monthHeader('November 2026', 1)} key="november2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="November 3, 2026" key="2026-11-03" style={panelStyle}>
      <p><strong>Seven New Translation Languages</strong></p>
      <ul>
        <li>You can now translate your books into <strong>Czech, Danish, Bosnian, Croatian, Finnish, Icelandic, and Bulgarian</strong>, with language-specific guidance and dialogue punctuation options.</li>
        <li>The translation language picker now puts <strong>Most popular</strong> languages first: German, French, Italian, Dutch, Brazilian Portuguese, and European Spanish. The remaining languages are listed alphabetically under <strong>Other languages</strong>.</li>
        <li>Newly launched languages show a <strong>NEW</strong> tag for 30 days. If your selected chapter or whole book is already translated into that language, <strong>Completed</strong> appears instead.</li>
      </ul>

      <p><strong>Cover Creation and Translation</strong></p>
      <ul>
        <li>Cover creation and translation now start with <strong>OpenAI Image 2.5 Sunburst</strong> when it is available. You can still choose another available model.</li>
      </ul>

      <p><strong>Translated EPUB Titles</strong></p>
      <ul>
        <li>New translated EPUB exports use your saved translated book title in the EPUB's book information when available, helping readers display the title for the selected edition.</li>
        <li>Export your book again to update an existing download. How the title appears may vary by reading app, especially if the book is already in your library.</li>
      </ul>

      <p><strong>French Translation and Proofreading</strong></p>
      <ul>
        <li>Improved French language guidance helps with commands, comparisons, and sentence structure while preserving the meaning and voice of your text.</li>
      </ul>

      <p><strong>Provider Directory</strong></p>
      <ul>
        <li>Providers can now submit a directory application without signing in to a ScribeShadow account.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* October 2026 */}
<Panel header={monthHeader('October 2026', 2)} key="october2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="October 25, 2026" key="2026-10-25" style={panelStyle}>
      <p><strong>More Reliable Whole-Book Proofreading</strong></p>
      <ul>
        <li>Once your whole-book proofreading request is accepted, remaining chapters continue entering processing even if you leave the page, refresh, or sign out.</li>
        <li><strong>Processing Status</strong> shows chapters being prepared alongside queued, active, completed, and failed chapters, making progress easier to follow on larger books.</li>
        <li>Reconnecting or retrying an interrupted submission resumes the accepted request without submitting the same chapters twice.</li>
      </ul>

      <p><strong>Glossary Scan and Translation Settings</strong></p>
      <ul>
        <li><strong>AI Glossary Scan</strong> now refreshes completed results when its live status update is missed, helping prevent a finished scan from continuing to appear in progress.</li>
        <li>Previously loaded glossary results remain available if a temporary connection problem interrupts a refresh.</li>
        <li>Translation confirmation shows your selected AI model by name, including <strong>GPT-6 Sol</strong> when it is also the recommended model.</li>
      </ul>
    </Panel>
    <Panel header="October 17, 2026" key="2026-10-17" style={panelStyle}>
      <p><strong>More Control over Translated Covers</strong></p>
      <ul>
        <li><strong>Additional text replacements</strong> let you translate individual book titles on a boxset cover, subtitles, and other cover wording alongside the main title and series name. Add up to ten original and replacement text pairs.</li>
        <li>Enhanced cover translation guidance and automatic checks help preserve text placement, heading sizes, and the exact replacement wording you choose.</li>
        <li>Your additional replacements are included in cover history and carried forward when you generate variations.</li>
        <li>The cover model picker includes newer <strong>OpenAI</strong> and <strong>Gemini</strong> image models, grouped by family with shorter, easier-to-read names. OpenAI appears first, followed by Gemini, with newer models first within each family.</li>
        <li>Full-image cover previews make it easier to compare artwork and text before choosing a translated cover.</li>
      </ul>

      <p><strong>Translation and Glossary Tools</strong></p>
      <ul>
        <li>Image-only sections, illustrations, and page breaks are accounted for when determining translation completion, without submitting those sections for paid text translation.</li>
        <li>Recognized embedded computer code receives additional protection for its syntax, indentation, strings, and comments while the surrounding prose is translated.</li>
        <li><strong>AI Glossary Scan</strong> can reuse a compatible completed scan without another scan charge. When a fresh paid scan is needed, you confirm it before continuing.</li>
        <li><strong>Guided Workflow</strong> coordinates your selected glossary scan and glossary application before starting chapter translations.</li>
        <li>Series glossary editing supports resolving existing conflicts a few entries at a time. Translation checks the completed glossary before proceeding.</li>
        <li>Enhanced <strong>Never Translate</strong> handling respects inline formatting and language-specific possessive forms while preserving your chosen names and phrases.</li>
      </ul>

      <p><strong>Proofreading and Character Context</strong></p>
      <ul>
        <li>Dialogue checks account for straight quotes and speech continuing across multiple paragraphs.</li>
        <li>Proofreading review includes enhanced positioning and validation of accepted suggestions, including edits across formatted text.</li>
        <li>French proofreading includes additional checks for narration consistency, dialogue attribution, gender agreement, and meaning across adjacent paragraphs.</li>
        <li>Whole-book proofreading can pause chapter submission during temporary service limits and resume as capacity becomes available.</li>
        <li>Character analysis includes additional recovery checks for difficult extraction results. Eligible older relationship records can receive a targeted compatibility refresh at no extra credit cost.</li>
        <li>While a character-list refresh is unavailable, you can continue viewing the last loaded cast and choose <strong>Retry cast refresh</strong>.</li>
      </ul>

      <p><strong>Exports and Book Management</strong></p>
      <ul>
        <li>Word exports support a wider range of images from imported documents. Use <strong>Include images</strong> to choose whether images appear in your Word download; saved chapters and assets stay unchanged.</li>
        <li>EPUB exports include enhanced handling of document structure and stylesheet links.</li>
        <li>Larger Word exports are prepared in the background, with the finished download available through processing status and export history.</li>
        <li>New exports distinguish changes to edition content from background book updates. Original exports also remain current when only the separate proofread text changes.</li>
        <li>Manuscript replacement retains your original and language-specific covers. Cover placeholders keep library cards readable when an image is unavailable.</li>
        <li>Source chapters stay protected from deletion while translation or other chapter work is active.</li>
      </ul>

      <p><strong>A Smoother Workspace and Checkout</strong></p>
      <ul>
        <li>Performance improvements help keep the workspace responsive during large submissions and exports, with more resilient model-information loading across translation, proofreading, and metadata tools.</li>
        <li>Language-edition selection and blurb editing include refinements that keep the displayed edition and unsaved-change indicators aligned with your actions.</li>
        <li>Guest checkout includes automatic credit-delivery retries, clear pending messages, and a manual retry option. Subscription renewal retries retain the same billing request.</li>
      </ul>

      <p><strong>Publishing Guides in Resources</strong></p>
      <ul>
        <li>The <strong>Resources</strong> library has separate tabs for Publishing Guides and Provider Directories.</li>
        <li>Read <strong>Tolino for Indie Authors: A Mini Handbook</strong> for guidance on German distribution, pricing, library services, and promotional opportunities.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* September 2026 */}
<Panel header={monthHeader('September 2026', 3)} key="september2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="September 24, 2026" key="2026-09-24" style={panelStyle}>
      <p><strong>GPT-6 Models Now Available</strong></p>
      <ul>
        <li><strong>GPT-6 Sol</strong> and <strong>GPT-6 Luna</strong> are now available in the AI model selectors.</li>
        <li><strong>Recommended setup</strong> now uses GPT-6 Sol for translation.</li>
        <li>GPT-5.6 Sol, Terra, and Luna have been removed from the selectable model list.</li>
      </ul>

    </Panel>
    <Panel header="September 16, 2026" key="2026-09-16" style={panelStyle}>
      <p><strong>Introducing the Marketing Tab</strong></p>
      <ul>
        <li>Your book now has a <strong>Marketing</strong> tab where you can explore localized marketing support for your translated editions, track Deep Dive orders, and open completed reports.</li>
        <li>To get started, select <strong>Deep Dive</strong> in the navigation to see your eligible books and translated editions. You can also open a book, choose a completed translation in a supported language, and select <strong>Marketing</strong>.</li>
      </ul>

      <p><strong>New M2M / Novel Publicity Partnership</strong></p>
      <ul>
        <li>ScribeShadow has partnered with <strong>Novel Publicity</strong> to offer a complete localized marketing report built from your translated manuscript: the <strong>Novel Publicity Deep Dive report</strong>.</li>
        <li>Each report includes a market entry brief, genre and market positioning, localized blurbs and taglines, Amazon keywords, categories, SEO subtitle guidance and metadata, and an ad campaign toolkit with foreign-market targeting and <strong>five video ad concepts</strong>.</li>
        <li>The ScribeShadow edition also includes <strong>English meanings beside localized copy</strong>, an <strong>exclusive excerpt library</strong> with English summaries, and an <strong>AI art brief with ten paste-ready image prompts</strong>: six built around ad angles and four for social images.</li>
        <li>Strategy and notes stay in English, while reader-facing copy is written for the target language and market. English meanings and the art brief are paid add-ons for public buyers; the excerpt library is available only with the ScribeShadow edition.</li>
        <li>The ScribeShadow-exclusive price is <strong>$50 per book, per language</strong>—a $75 value. Payment is completed at Novel Publicity checkout.</li>
        <li>You review your book details and authorize sharing the selected translated manuscript before continuing to checkout. Novel Publicity emails your finished report, and it is also saved in ScribeShadow for access from your book's Marketing tab.</li>
      </ul>
    </Panel>
    <Panel header="September 12, 2026" key="2026-09-08" style={panelStyle}>
      <p><strong>Translate Your Series Blurb in ScribeShadow</strong></p>
      <ul>
        <li>You can now translate, review, and edit your series blurb in <strong>Series Management → Description translations</strong>, keeping your series descriptions together in one place.</li>
        <li>Choose a target language and AI model, or enter your own translation. Saved descriptions are shared across the books linked to your series.</li>
        <li>The first AI translation for each language pair is included. Replacing an existing translation with AI uses credits and asks for confirmation; manual edits are free.</li>
        <li>You can also view the translated series description in a book's Metadata tab and select <strong>Manage in Series</strong> to make changes.</li>
      </ul>

      <p><strong>More Consistent French Translations</strong></p>
      <ul>
        <li>Clearer explanations help you choose between <strong>Modern/Accessible</strong> and <strong>Classical/Literary</strong> narration. French style choices are also available in Quick setup, including when translating into several languages at once.</li>
        <li><strong>AI Detect</strong> now shows its recommendation for you to confirm before translation. Your saved or manually selected style stays in place.</li>
        <li>Improved checks help keep French narration in your chosen style while respecting dialogue, flashbacks, and changes of tense in your original manuscript.</li>
        <li>French dialogue and gender agreement have been improved, including how dialogue identifies who a character is speaking to.</li>
        <li>These improvements also apply when you proofread a chapter or your whole book, with corrections included in your usual proofreading suggestions.</li>
      </ul>

      <p><strong>Clearer Glossary Conflict Messages</strong></p>
      <ul>
        <li>If a term has competing <strong>Always Translate</strong> entries within the same book, series, or account glossary, a clearer message identifies the conflicting choices so you can resolve them.</li>
        <li>These checks help catch conflicting terminology before translation begins, supporting more consistent names, titles, and phrases throughout your book.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* August 2026 */}
<Panel header={monthHeader('August 2026', 5)} key="august2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="August 23, 2026" key="2026-08-23" style={panelStyle}>
      <p><strong>Protected Names and Phrases Across More Languages</strong></p>
      <ul>
        <li>Names and phrases marked <strong>Never Translate</strong> are now handled more reliably when a language attaches possessive endings, particles, or other short grammatical forms directly to them.</li>
        <li>This improves support for Dutch, German, Swedish, Norwegian, Turkish, Japanese, Korean, Chinese, Thai, and Hebrew translations.</li>
        <li>These language-specific forms can remain natural while the protected name or phrase itself stays unchanged.</li>
        <li>This also reduces chapter failures that previously identified a correctly preserved name as the affected Never Translate rule.</li>
      </ul>

      <p><strong>Check Chapter Detection Before Upload</strong></p>
      <ul>
        <li>Attaching a Word or EPUB manuscript now automatically shows how many chapter sections ScribeShadow will import.</li>
        <li>A green or amber message helps you spot formatting problems before upload, with direct links to formatting guidance when the count needs attention.</li>
        <li>The chapter check is informational and never prevents you from continuing with the upload.</li>
        <li>Selecting a different manuscript automatically replaces the current file and checks the new one without an extra confirmation step.</li>
      </ul>

      <p><strong>Correct Language Settings in Translated Exports</strong></p>
      <ul>
        <li>Translated Word and EPUB exports now set their document language to the translation's target language.</li>
        <li>This helps screen readers and text-to-speech tools use the correct pronunciation and enables language-appropriate spellcheck.</li>
        <li>Completed translations can be exported again to receive the corrected language settings.</li>
      </ul>
    </Panel>
    <Panel header="August 18, 2026" key="2026-08-18" style={panelStyle}>
      <p><strong>A Clearer Book Workspace</strong></p>
      <ul>
        <li><strong>Simple</strong> and <strong>Advanced</strong> are now <strong>Guided Workflow</strong> and <strong>All Tools</strong></li>
        <li>Quick editing for book details and covers is now available in the Guided Workflow.</li>
        <li>In All Tools, the active language workspaces appear first, unused languages are grouped under <strong>Add or view languages</strong>, and secondary sections start collapsed.</li>
      </ul>

      <p><strong>Easier Proofreading</strong></p>
      <ul>
        <li>Clearer action labels and stronger chapter tabs make proofreading tools easier to find.</li>
        <li>The review panel now stays beside the chapter, with sticky decision totals and save controls.</li>
        <li>Unsaved decisions are restored on the same device, and display errors now include retry and help options.</li>
      </ul>

      <p><strong>Pricing, Billing, and Support</strong></p>
      <ul>
        <li>A new Pricing calculator estimates credits and purchase options by word count, languages, proofreading, and AI Glossary Scan choices—no upload or account required.</li>
        <li>Subscription actions are easier to find, and support article links and videos now open correctly.</li>
      </ul>

    </Panel>
    <Panel header="August 15, 2026" key="2026-08-15" style={panelStyle}>
      <p><strong>More Reliable Sign-In</strong></p>
      <ul>
        <li>Sign-in is now more reliable in Safari, including when you have multiple ScribeShadow tabs open.</li>
        <li>Email and Google sign-in no longer stay stuck on a loading spinner when part of your account takes longer to load.</li>
        <li>If your browser cannot restore or confirm your session promptly, ScribeShadow now provides a clear message so you can try again.</li>
        <li>Session recovery has also been improved, reducing unnecessary sign-outs when a saved session needs to be refreshed.</li>
      </ul>

      <p><strong>More Dependable Translation and Proofreading</strong></p>
      <ul>
        <li>Names and phrases marked <strong>Never Translate</strong> are now protected more reliably throughout translation and proofreading.</li>
        <li>If a protected phrase still causes a chapter to stop, the message now identifies the affected rule so it is easier to review and correct.</li>
        <li>Dialogue punctuation and spacing have been improved, especially for Italian and European Spanish.</li>
        <li>Formatting checks are now more flexible when a language naturally needs different paragraph breaks, while still protecting the chapter from accidental formatting loss.</li>
        <li>When a translation needs another attempt, ScribeShadow avoids repeating the same unsuccessful correction.</li>
      </ul>

      <p><strong>Proofread Reviews Are Easier to Complete</strong></p>
      <ul>
        <li>A single suggestion that can no longer be matched to the saved chapter will not prevent you from saving your other review decisions.</li>
        <li>Unmatched suggestions are treated as not accepted, so you can choose which remaining changes to keep without rerunning proofreading.</li>
        <li>Older saved reviews are checked when opened, allowing suggestions to be restored when they can still be matched safely.</li>
        <li>Proofreading now does a better job of preserving chapter formatting, protected phrases, and language-appropriate dialogue.</li>
      </ul>
    </Panel>
    <Panel header="August 11, 2026" key="2026-08-11" style={panelStyle}>
      <p><strong>More Reliable Chapter Translations</strong></p>
      <ul>
        <li>More chapters now complete successfully without needing another translation attempt.</li>
      </ul>

      <p><strong>Find Books by Translation Language</strong></p>
      <ul>
        <li>Manage Books now includes a <strong>Translations</strong> filter, making it easier to find books by target language.</li>
        <li>You can quickly see which translations are <strong>Complete</strong>, <strong>In progress</strong>, or <strong>Not started</strong>.</li>
        <li>Combine translation filters with search, Author, and Series, then clear everything with one action.</li>
      </ul>

      <p><strong>Faster Large-Library Browsing</strong></p>
      <ul>
        <li>Manage Books loads translation progress more quickly, especially for larger libraries.</li>
        <li>Switching between translation filters now feels faster and more responsive.</li>
        <li>If translation progress is temporarily unavailable, you will see a clear status instead of an endless loading indicator.</li>
      </ul>
    </Panel>
    <Panel header="August 2, 2026" key="2026-08-02" style={panelStyle}>
      <p><strong>More Reliable AI Glossary Suggestions</strong></p>
      <ul>
        <li>AI Glossary Scan now retries any fixed-translation recommendation that is missing its translation, using manuscript excerpts and the scan’s fallback model before asking you to review it.</li>
        <li>An empty translation field now includes <strong>Generate</strong>, which requests context-aware target-language options instead of asking the AI to reconsider whether the term should be translated.</li>
        <li>Generated translation explanations are written in the manuscript’s original language, so an English author can understand why a Spanish, French, or other target-language option was suggested.</li>
        <li>Repeated terms found in multiple chapters are consolidated into one review item with combined occurrence counts, chapter references, and manuscript context.</li>
        <li>Glossary choices control consistency; dismissing a suggestion does not leave manuscript text untranslated. The chapter translator still translates the term normally in sentence context.</li>
      </ul>

      <p><strong>A Faster, Clearer Review Experience</strong></p>
      <ul>
        <li>The review window now has a clearer progress summary, status tabs, compact search and sorting controls, and a more focused bulk-action area.</li>
        <li>Suggestions are separated into bordered rows with shorter, neutral recommendation labels such as <strong>Translate</strong>, <strong>Keep original</strong>, and <strong>Review needed</strong>.</li>
        <li>Decision, translation, option, Generate, and Apply controls are aligned consistently, while manuscript context remains available from a compact expandable link.</li>
        <li><strong>Auto-apply confident AI</strong> uses the same 85% confidence policy as pre-translation glossary automation and tells you how many terms will remain for review.</li>
        <li>Unsaved custom choices are confirmed and saved before Auto Apply, and previously applied individual rules are preserved rather than overwritten.</li>
        <li>The scope control now makes it clearer whether a glossary rule will be saved to the book, series, or account.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* July 2026 */}
<Panel header={monthHeader('July 2026', 3)} key="july2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="July 30, 2026" key="2026-07-30" style={panelStyle}>
      <p><strong>A Stronger Recommended Translation Setup</strong></p>
      <ul>
        <li>The <strong>Recommended setup</strong> now uses ChatGPT-5.6 Terra, balancing strong multilingual translation quality with more efficient translation costs.</li>
        <li>Our testing found that this direct translation setup outperformed the previous model-plus-quality-pass workflow, so Recommended translations no longer run a separate automatic <strong>Additional Quality Pass</strong>.</li>
        <li>Automated validation, alignment checks, and language-specific formatting safeguards remain included.</li>
        <li>If you want a separate rewriting pass, you can still enable <strong>Additional Quality Pass</strong> under <strong>Adjust settings</strong>. The optional pass adds 15% to the translation credit cost.</li>
      </ul>
      <p><strong>Simpler AI Model Choices</strong></p>
      <ul>
        <li>AI model choices no longer use <strong>Good</strong>, <strong>Better</strong>, or <strong>Best</strong> rankings. Model names are now presented directly, making it easier to choose the option you want.</li>
        <li>The <strong>Recommended setup</strong> remains the simplest choice and automatically uses the model selected through our multilingual quality testing.</li>
      </ul>
      <p><strong>All Supported Languages Are Now Generally Available</strong></p>
      <ul>
        <li>Japanese, Korean, Traditional Chinese, Simplified Chinese, Thai, Hindi, Catalan, Afrikaans, Hebrew, Norwegian, Serbian, and Turkish have graduated from beta.</li>
        <li>All supported languages now appear in the standard language selectors without beta labels or enrollment requirements.</li>
      </ul>
      <p><strong>Safer Chapter Deletion</strong></p>
      <ul>
        <li>The chapter list no longer offers source-chapter deletion while you are viewing a translated-language workspace.</li>
        <li>Source chapters can only be deleted from the <strong>Original</strong> workspace, preventing a translated chapter action from accidentally removing the original chapter and its translations.</li>
        <li>Deletion warnings now state more clearly that deleting a source chapter also permanently deletes every matching translation.</li>
      </ul>
    </Panel>
    <Panel header="July 5, 2026" key="2026-07-05" style={panelStyle}>
      <p><strong>AI Glossary Scan Word Export</strong></p>
      <ul>
        <li>AI Glossary Scan results can now be exported to a Word document from the scan review panel.</li>
        <li>The export includes source terms, recommended glossary actions, target suggestions, confidence, occurrence counts, chapter references, sample context, and review status.</li>
        <li>Word exports use a compact table layout with limited chapter references and shortened samples so long context passages do not overwhelm the document.</li>
        <li>This makes it easier to share glossary scan output with authors, translators, or review partners when checking terminology consistency across a series.</li>
      </ul>

      <p><strong>AI Glossary Scan Review Window</strong></p>
      <ul>
        <li>The AI Glossary Scan review window is now roomier and easier to scan when reviewing many suggested translation pairs.</li>
        <li>You can expand the review window to see more suggestions at once, then restore it when you are done.</li>
        <li>The suggestions list now keeps its own scroll area, so the bottom of the list remains reachable without the modal running off-screen.</li>
        <li>The review layout uses the available space more cleanly, reducing empty horizontal space while keeping filters, bulk actions, and per-term Apply buttons visible.</li>
      </ul>

      <p><strong>Recommended Setup Wording</strong></p>
      <ul>
        <li>The advanced translation window now labels the bundled quick setup option as <strong>Recommended setup</strong>, making it clearer that the option includes both the model choice and the included quality pass.</li>
      </ul>
    </Panel>
    <Panel header="July 2, 2026" key="2026-07-02" style={panelStyle}>
      <p><strong>Provider Directory</strong></p>
      <ul>
        <li>Added a new <strong>Resources</strong> area with a human review provider directory for authors who want an independent review step after using ScribeShadow.</li>
        <li>Provider cards include services, contact details, websites, logos, and informational badges such as ScribeShadow workflow familiarity, user discounts, referral participation, or education contributions when applicable.</li>
        <li>Directory listings are informational only. Providers are independent third-party businesses, and inclusion does not create an official provider relationship, endorsement, certification, or guarantee of service quality.</li>
        <li>Providers can now request listing review from the directory using an in-app application form with service areas, badge review requests, pricing, turnaround, language coverage, and workflow details.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* June 2026 */}
<Panel header={monthHeader('June 2026', 2)} key="june2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="June 14, 2026" key="2026-06-14" style={panelStyle}>
      <p><strong>Simple Translation Clarifications</strong></p>
      <ul>
        <li>Simple Mode now explains AI Glossary Scan behavior more clearly: when selected, it runs before chapter translation and uses automatic terminology choices for that Simple translation.</li>
        <li>The completed Simple translation summary now shows AI Glossary Scan as <strong>Automatic choices applied</strong> instead of implying there is a separate review step for Simple Mode.</li>
        <li>Authors who want to review or edit glossary choices before translation should use Advanced Mode before starting the translation.</li>
      </ul>

      <p><strong>Blurb Translation Follow-up</strong></p>
      <ul>
        <li>When a Simple translation is complete but the translated blurb is missing, the completed summary now shows <strong>Not translated</strong> with a direct <strong>Translate blurb</strong> action.</li>
        <li>The blurb action opens a focused blurb-only translation window for the completed target language, while keeping title and series name unchanged.</li>
        <li>If the book has no saved source blurb, the Simple summary now says <strong>No source blurb</strong> instead of showing a generic not-selected state.</li>
      </ul>

      <p><strong>Proofread Review Fix</strong></p>
      <ul>
        <li>Fixed an issue where <strong>Apply Pending</strong> could incorrectly report that there were no pending proofread reviews for the original-language version.</li>
        <li>Original-language pending proofreading changes now apply from the Book Dashboard using the correct <strong>Original</strong> version.</li>
      </ul>

      <p><strong>Proofread Explanation Language</strong></p>
      <ul>
        <li>The Proofread window now includes an <strong>Explanation language</strong> setting for correction reasons, separate from the manuscript language being proofread.</li>
        <li>Your explanation language choice is saved for future proofread runs, so you can proofread a manuscript in one language while reading suggested-change reasons in another.</li>
      </ul>

      <p><strong>Export Button Visibility</strong></p>
      <ul>
        <li>The chapter workspace now shows <strong>Export</strong> as a visible toolbar button again, so you no longer need to open <strong>More</strong> to export a translated book.</li>
      </ul>
    </Panel>
    <Panel header="June 11, 2026" key="2026-06-11" style={panelStyle}>
      <p><strong>Simple Mode</strong></p>
      <ul>
        <li>Simple Mode is a streamlined way to translate a book and prepare an export from one focused workflow.</li>
        <li>Choose a target language, export format, localized title and series options, and optional add-ons before starting.</li>
        <li>Simple Mode shows credit estimates, readiness checks, and progress in one place so it is easier to know what will happen before you confirm.</li>
      </ul>

      <p><strong>Free AI Metadata Help</strong></p>
      <ul>
        <li><strong>Free service:</strong> AI metadata suggestions can help fill in genres, tropes, keywords, and intended audience without using credits.</li>
        <li>The first keyword suggestion run for each book and target language is now free.</li>
        <li>The first blurb translation for each book and target language is now free, including when used from Simple Mode.</li>
        <li>Blurb translations now have a 1,000-word source blurb limit to keep the free first run fair and prevent accidental chapter-length submissions.</li>
        <li>Localized title and series-name options are now easier to review before applying them to a translated edition.</li>
        <li>The new review step lets you choose what to apply instead of replacing metadata automatically.</li>
      </ul>

      <p><strong>Book Workspace Refresh</strong></p>
      <ul>
        <li>The book detail page has a refreshed workspace with clearer language dashboards, progress summaries, quick actions, and guided tours.</li>
        <li>Language cards now make it easier to see which editions are ready, in progress, or need attention.</li>
        <li>Quick links help you jump to common next steps like translation, proofreading, metadata, covers, exports, and support.</li>
      </ul>

      <p><strong>Metadata Workspace Refresh</strong></p>
      <ul>
        <li>Original and translated metadata editing has been reorganized into clearer storefront sections.</li>
        <li>Title, series, blurb, glossary rules, keywords, covers, and reader targeting are easier to review from the metadata workspace.</li>
        <li>Keyword suggestions now show which metadata sources are being used, so you can improve the inputs before generating suggestions.</li>
        <li>AI Glossary Scan review wording is clearer: <strong>Skip</strong> was replaced with <strong>Do not add rule</strong> and dismissed suggestion labels so authors do not think the word will be omitted from translation.</li>
      </ul>

      <p><strong>Character and Relationship Analysis Improvements</strong></p>
      <ul>
        <li>The Characters workspace now has clearer readiness summaries for cast names, aliases, Grammar Gender, and relationship guidance before translation.</li>
        <li>The first full-book cast and relationship analysis is free, and reruns now explain the credit cost before you start.</li>
        <li>Advanced relationship controls now make it easier to review and update Formal / Informal Speech rules, chapter-level relationship rows, aliases, and merged character names.</li>
      </ul>

      <p><strong>Library Improvements</strong></p>
      <ul>
        <li>The book library now includes clearer sorting and grouping options for organizing books by title, author, or series.</li>
        <li>Translation status flags have been refreshed so completed and in-progress languages are easier to scan.</li>
      </ul>

      <p><strong>Credit History Improvements</strong></p>
      <ul>
        <li>Credit History now includes target-language filtering.</li>
        <li>Filtered totals and grouped summaries by book and language make it easier to audit credit usage.</li>
      </ul>

      <p><strong>Support, FAQ, and Updates</strong></p>
      <ul>
        <li>Support has been refreshed with better search, category filters, top answers, tutorials, and contact guidance.</li>
        <li>The Tutorials section now includes a refreshed video set for upload, manuscript review, glossary, translation, and proofreading workflows.</li>
        <li>A new On Break help article explains how the plan works and where to manage it.</li>
        <li>The About and Updates pages are easier to browse, with improved FAQ filtering and update history navigation.</li>
      </ul>

      <p><strong>Billing and Pricing Refresh</strong></p>
      <ul>
        <li>Account Billing now shows clearer plan, credit, renewal, and billing-action details.</li>
        <li>Plan comparison and subscription change screens have been refreshed to make choices easier to review.</li>
      </ul>

      <p><strong>Translation, Export, and Cover Flow Improvements</strong></p>
      <ul>
        <li>Translation and export windows now include clearer readiness checks, credit breakdowns, warnings, and guided tours.</li>
        <li>Cover selection now makes it easier to compare the current cover with generated options and verify translated cover text before saving.</li>
      </ul>

      <p><strong>Audiobook Workflow Refresh</strong></p>
      <ul>
        <li>The Audiobook workspace now has a clearer overview for chapter audio, intro and outro credits, full audiobook files, and YouTube video status.</li>
        <li>Audiobook creation now includes clearer voice selection, chapter eligibility, intro and outro options, credit totals, and readiness messages before starting.</li>
        <li>Audiobook export now makes it easier to prepare MP3 or M4B packages, including chapter-by-chapter files or one full-audiobook file when available.</li>
        <li>YouTube video generation now has clearer cover, resolution, credit, and readiness checks before creating the video.</li>
      </ul>

      <p><strong>Chapter Editing Now Available</strong></p>
      <ul>
        <li>Manual chapter editing has moved out of beta and is now available without joining the beta program.</li>
        <li>You can edit original, translated, and proofread chapter text directly in the editor when that content already exists.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* May 2026 */}
<Panel header={monthHeader('May 2026', 1)} key="may2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="May 2, 2026" key="2026-05-02" style={panelStyle}>
      <p><strong>On Break Plan</strong></p>
      <ul>
        <li><strong>On Break</strong> is a lower-cost subscriber-only plan for times when you want to keep your subscription active but do not need new monthly credits.</li>
        <li>On Break grants <strong>0 new monthly credits</strong>, but your existing subscription credits stay protected while you remain subscribed.</li>
        <li>To use it, open <strong>Account &gt; Billing</strong>, choose <strong>Move to On Break</strong>, review the details, and confirm. The change will be scheduled for your next billing date.</li>
        <li>If On Break is scheduled and you change your mind before it starts, use <strong>Cancel On Break Change</strong> from Account Billing.</li>
        <li>When you are ready to return, choose <strong>Resume Plan</strong> from Account Billing to go back to your stored return plan.</li>
      </ul>

      <p><strong>Legacy Plans</strong></p>
      <ul>
        <li>If you move directly from a legacy plan to On Break, ScribeShadow stores a return entitlement for that exact legacy plan.</li>
        <li>Use <strong>Return to Legacy Plan</strong> from Account Billing when you are ready to resume that saved legacy plan.</li>
        <li>Choosing a different current plan instead of the saved legacy return plan forfeits the legacy return option.</li>
      </ul>

      <p><strong>Credits and Cancellation</strong></p>
      <ul>
        <li>Subscription credits do not expire while you stay subscribed, including while you are on On Break.</li>
        <li>If you cancel your subscription and access ends, remaining subscription credits stay usable for <strong>60 days</strong>, then expire.</li>
        <li>Full cancellation also removes any On Break return entitlement, including a saved legacy-plan return option.</li>
        <li>Forever Credits still never expire.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* April 2026 */}
<Panel header={monthHeader('April 2026', 4)} key="april2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="April 29, 2026" key="2026-04-29" style={panelStyle}>
      <p><strong>Cleaner Announcements</strong></p>
      <ul>
        <li>In-app announcements now take up less space and feel more integrated with the rest of ScribeShadow.</li>
        <li>Announcement text is easier to scan, with a cleaner layout and a more subtle visual style in both light and dark mode.</li>
        <li>Important update notices still remain easy to spot, but they should no longer distract from the page you are working on.</li>
      </ul>

      <p><strong>Proofread Review Workflow</strong></p>
      <ul>
        <li>You can now apply pending proofreading changes for the original language from the Book Dashboard, matching the bulk accept flow already available for translated languages.</li>
      </ul>

      <p><strong>Model Availability</strong></p>
      <ul>
        <li>Claude 4.6 is now available as an AI model option.</li>
      </ul>
    </Panel>
    <Panel header="April 27, 2026" key="2026-04-27" style={panelStyle}>
      <p><strong>Cover Generation Updates</strong></p>
      <ul>
        <li>Cover generation and cover translation now use newer image options for stronger results.</li>
        <li>The discard confirmation in cover selection now better matches dark mode.</li>
        <li>Translate Cover now includes an optional cover edit guidance field for short requests about the translated cover.</li>
        <li>You can use it for requests like keeping the original mood, adjusting sticker size, or preserving the overall feel while changing cover text.</li>        
        <li>Translate and Generate Cover windows should now open more quickly after clicking their buttons.</li>
      </ul>

    </Panel>
    <Panel header="April 24, 2026" key="2026-04-24" style={panelStyle}>
      <p><strong>Proofread Review Workflow</strong></p>
      <ul>
        <li>You can now apply pending proofreading changes for a full language at once instead of opening each chapter individually.</li>
        <li>The Proofread window now includes an option to automatically accept future proofreading changes, and your choice is saved for later runs.</li>
        <li>When pending proofreading changes exist, they are now surfaced more clearly in the language dashboard and chapter list so they are easier to find.</li>
      </ul>

      <p><strong>Proofread Progress & Reliability</strong></p>
      <ul>
        <li>Dashboard progress now counts chapters that are waiting in proofread review, so proofread percentages no longer look incomplete when a book is ready for review.</li>
        <li>Opening proofread review content is now more reliable for saved pending reviews.</li>
        <li>When older saved suggestions can no longer be applied cleanly, the system now keeps whatever valid proofread result it can instead of leaving the chapter looking unproofread.</li>
      </ul>

    </Panel>
    <Panel header="April 21, 2026" key="2026-04-21" style={panelStyle}>
      <p><strong>French Translation Improvements</strong></p>
      <ul>
        <li>English-to-French translation now does a better job handling French dialog, so punctuation, structure, and speaker flow should feel more natural.</li>
        <li>New French translations now include these dialog improvements automatically.</li>
      </ul>

      <p><strong>Free Legacy French Dialog Update</strong></p>
      <ul>
        <li>If you have an older English-to-French translation, you may now see <strong>Apply Updated French Dialog Rules</strong> in the French dashboard.</li>
        <li>This gives eligible older translations a <strong>free, one-time cleanup option</strong> to improve French dialog handling without rerunning the whole book.</li>
        <li>If an older translation would benefit from a bigger refresh, eligible books may also apply a discounted retranslation option.</li>
      </ul>

      <p><strong>Multiple Proofreading Passes & Review Controls</strong></p>
      <ul>
        <li>You can now manage repeated proofreading more clearly: the proofread window explains whether you are starting a first pass, starting a new pass, or replacing an unsaved review.</li>
        <li>If a chapter already has review changes waiting for you, the button text and warning message now make it much clearer when starting again will replace that pending review.</li>
        <li>When proofreading a full book, chapters that already have a saved proofread or a pending proofread review are now skipped automatically, with clearer messages about what will and will not be included.</li>
        <li>Standard proofreading now costs <strong>30% of the chapter word count</strong>, making it less expensive to run a proofreading pass. Editing notes pricing is unchanged.</li>
      </ul>

      <p><strong>Better Results with Recommended Translation Settings</strong></p>
      <ul>
        <li>Translations should now read more smoothly overall when you use the <strong>Recommended setup</strong>.</li>
        <li>If you choose a different model, turning on the optional <strong>Additional Quality Pass</strong> can also improve the final result.</li>
        <li>These improvements are aimed at making translations feel more polished and natural across the full chapter, not just line by line.</li>
      </ul>

      <p><strong>Metadata & Keyword Navigation</strong></p>
      <ul>
        <li><strong>Translate Metadata</strong> now follows the translated language you are currently viewing, and if a translated title, series name, or blurb already exists in that language, you will now see a confirmation before anything is replaced.</li>
        <li>Translated-language metadata now includes a <strong>Suggest Keywords</strong> action right in the Keywords section, so you can launch keyword suggestions without leaving that language's metadata view.</li>
        <li>The translated-language dashboard now includes <strong>Suggest Keywords</strong> in both <strong>Next steps</strong> and <strong>Quick links</strong> for faster access.</li>
        <li>The translated-language dashboard now includes <strong>Run AI Glossary Scan</strong> in <strong>Quick links</strong>, so you can launch glossary review work directly from that language's dashboard.</li>
        <li>The book title in the header now opens metadata directly, with clearer hover feedback and tooltip help so it is easier to jump into metadata quickly.</li>
        <li>The old pencil shortcut beside the title was replaced with a more intuitive metadata/form icon.</li>
      </ul>

      <p><strong>Cover Generation Refresh</strong></p>
      <ul>
        <li>The Gemini cover option now uses a newer image model, which should produce stronger translated cover results overall.</li>
        <li>The cover generation window was reorganized to feel cleaner and easier to follow.</li>
        <li>Key choices are now grouped more clearly (language, mode, model, text replacements, rights confirmation, and image count), so it is faster to review your setup before generating.</li>
      </ul>

      <p><strong>Word Import & Export Formatting</strong></p>
      <ul>
        <li>Formatting is now preserved more reliably when importing from Word and exporting back to Word.</li>
        <li>Paragraph layout should remain closer to your original document so cleanup after export is reduced.</li>
      </ul>

      <p><strong>AI Glossary Review Improvements</strong></p>
      <ul>
        <li>You can now see where a glossary decision was applied when reviewing AI Glossary Scan results (Book, Series, or Account).</li>
        <li>You can now move glossary terms in bulk between <strong>Book</strong> and <strong>Series</strong> from the glossary screen, so cleanup is much faster.</li>
        <li>Bulk move actions follow your current search and filters, so you can move exactly the entries you are looking at.</li>
        <li>Translation warnings were rewritten to be clearer: terms that are still pending review will still be translated, but their <strong>Always Translate</strong> / <strong>Never Translate</strong> rules are not enforced until you review and apply them.</li>
      </ul>

	      <p><strong>Audience & Character Consistency Updates</strong></p>
	      <ul>
	        <li>You can now use additional intended-audience options, including <strong>MM Romance Readers</strong> and <strong>FF Romance Readers</strong>, to apply targeted translation guidance for those audiences.</li>
	        <li><strong>Clean Romance Readers</strong>, <strong>MM Romance Readers</strong>, and <strong>FF Romance Readers</strong> now have clearer in-app help text so you can see exactly how those settings affect translation behavior.</li>
	        <li>Character gender consistency rules were strengthened to better protect pronouns, agreement, and gendered references from unintended flips during translation and proofreading.</li>
	        <li>French proofreading now gives clearer guidance about choosing the right narrative voice, so it is easier to keep the full book consistent when proofreading in French.</li>
	        <li>Hindi translations now do a final script cleanup pass before finishing, so leftover Latin-script names and words are more consistently converted into Devanagari.</li>
	      </ul>

      <p><strong>Delete Language Content Now Clears Related Exports</strong></p>
      <ul>
        <li>When you delete translated chapters for a language, related exported files and export links for that language are now removed automatically.</li>
        <li>When you delete proofread chapters for a language, proofread export files and links for that language are now removed automatically too.</li>
        <li>Recent Export lists in the dashboard/export views and related Processing Status export links now clear right away, so stale download links are no longer shown.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* March 2026 */}
<Panel header={monthHeader('March 2026', 1)} key="march2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="March 3, 2026" key="2026-03-03" style={panelStyle}>
      <p><strong>Character Split (Self-Service)</strong></p>
      <ul>
        <li>You can now separate merged characters directly from Character Management without contacting support.</li>
        <li>Use the new branch action on an alias to open <strong>Separate merged characters</strong>, then confirm the split.</li>
        <li>After a split, related character analysis updates are queued automatically so your character and relationship views stay in sync.</li>
        <li>Split actions are blocked while active translation or proofreading jobs are running, with clearer messages explaining why.</li>
      </ul>

      <p><strong>Translation Reliability</strong></p>
      <ul>
        <li>When using the recommended model, fewer chapter translation failures should occur.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* February 2026 */}
<Panel header={monthHeader('February 2026', 4)} key="february2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="February 26, 2026" key="2026-02-26" style={panelStyle}>
      <p><strong>Translation Option: Skip Character Analysis</strong></p>
      <ul>
        <li>You can now choose to translate without waiting for character analysis in single-language runs.</li>
        <li>In the Translate modal, click <strong>Adjust settings</strong>, then under <strong>Character Analysis</strong> uncheck <strong>Run character analysis before translating.</strong></li>
        <li>To set your default behavior, open <strong>Account Settings</strong> and toggle <strong>Require character analysis by default</strong>.</li>
        <li>Leave this enabled (default) if you want stronger pronoun/formality consistency from relationship context.</li>
      </ul>

      <p><strong>Glossary Count Bug</strong></p>
      <ul>
      <li>Fixed a bug where series level Always / Never Translate counts weren't always showing in the book level UI.</li>
      </ul>
    </Panel>
    <Panel header="February 23, 2026" key="2026-02-23" style={panelStyle}>
      <p><strong>Translation Improvements</strong></p>
      <ul>
        <li>You can now translate a book into up to <strong>3 languages at once</strong>, so you no longer need to start each language separately.</li>
        <li>If you do not have enough credits at translation time, you can now <strong>top up credits right in the translation flow</strong> without going to the Account screen.</li>
        <li>The Translate Book screen now includes a <strong>View details</strong> option, so you can review your selections before you start.</li>
        <li>The Target Languages dropdown now shows which languages are already completed, so it is easier to choose what still needs translation.</li>
      </ul>

      <p><strong>Changes</strong></p>
      <ul>
        <li>When character analysis fails, customer-facing messages are now cleaner and focus on what to do next (retry or contact support).</li>
        <li>If character analysis has already failed, translation requests now stop quickly instead of sitting in a blocked state.</li>
        <li>Behind the scenes, translation reliability was tightened with better blocked-job recheck scheduling, improved retry handling, and stricter final proofread alignment checks.</li>
        <li>The Billing page now shows subscription credit protection and any post-cancellation expiration dates with your local <strong>date, time, and timezone</strong>.</li>
      </ul>
      <p><strong>Bug Fixes</strong></p>
      <ul>
        <li>Fixed an export issue where the downloaded file could download the wrong language file in some cases. Exports now use the exact language you pick.</li>
        <li>Removed a misleading chapter-translation warning that could appear even when you were allowed to continue.</li>
      </ul>      
    </Panel>
    <Panel header="February 14, 2026" key="2026-02-14" style={panelStyle}>
      <p><strong>Character Tools</strong></p>
      <ul>
        <li>Character relationship rows can now be sorted by <strong>Default order</strong>, <strong>Character A</strong>, or <strong>Character B</strong> to make long lists easier to scan.</li>
        <li>When a character gender conflict is found, you can now use one-click <strong>Resolve as Masculine</strong> or <strong>Resolve as Feminine</strong> actions and get a clear summary of what was updated.</li>
        <li>Relationship wording and labels were rewritten to be clearer, including who is speaking to whom, so reviewing chapter-level details is faster.</li>
        <li>Formal/informal relationship settings are now easier to review and update, especially when each direction uses different speech style.</li>
        <li>Chapter relationship rows now show direction and formality more clearly for faster pass-through review.</li>
        <li>Character and relationship sections now highlight <strong>Needs review</strong> more clearly when action is required.</li>
        <li>The extra “Character analysis is up to date” status card was removed to reduce clutter when nothing needs attention.</li>
      </ul>

      <p><strong>AI Glossary Scan</strong></p>
      <ul>
        <li>Before translation, you can now choose to have the AI Glossary Scan run automatically.</li>
        <li>When automatic scan is selected, the expected cost now reflects that choice, and wording was updated to better explain what gets auto-applied.</li>
        <li>Automatic scan applies high-confidence glossary suggestions.</li>
        <li>Fixed an issue where scan warnings could still show pending items after review was already complete.</li>
      </ul>

      <p><strong>Translation & Export</strong></p>
      <ul>
        <li>Proofread exports work when only part of a book is proofread: completed proofread chapters are used, and remaining chapters fall back to unproofread chapters automatically so export can continue.</li>
        <li>Export notices now clearly list which chapters will use fallback (unproofed) text.</li>
        <li>Translate actions in book pages were simplified into one menu with two choices: <strong>Translate Book</strong> and <strong>Translate Metadata</strong>.</li>
      </ul>

      <p><strong>Covers, Audio, and Language Defaults</strong></p>
      <ul>
        <li>Cover previews now show a source label under the image, so you can tell whether you are viewing the original-language cover, a translated-language cover, or a translated placeholder that is currently using the original cover.</li>
        <li>Fixed a cover issue where removing a translated cover could fail repeatedly with an unclear error message.</li>
        <li>Default language settings now clearly explain your choices: <strong>Always start empty</strong> (you choose each time), <strong>Use current context</strong> (uses the language you are currently viewing), or <strong>Always use specific language</strong> (always preselects one language you choose).</li>
        <li>Translate, Proofread, and Export now show one clear message when a required language choice is missing.</li>
        <li>The Audiobook area was reorganized into a cleaner single workspace with improved status visibility, filtering, and a simpler distribution flow.</li>
      </ul>
    </Panel>
    <Panel header="February 8, 2026" key="2026-02-07" style={panelStyle}>
      <p><strong>Characters & Relationships</strong></p>
      <ul>
        <li>A new Character Relationship workflow helps you review character names, aliases, and grammar gender in one place.</li>
        <li>Relationship cards summarize Formal/Informal speech by character pair, so it’s easier to keep dialogue consistent.</li>
        <li>Bulk updates let you set Formal/Informal defaults across chapters and mark relationships as reviewed.</li>
      </ul>

      <p><strong>AI Glossary Scan Review</strong></p>
      <ul>
        <li>AI Glossary Scan suggestions include a full review panel with clear status counts for pending, applied, and ignored items.</li>
        <li>Search, filter, and sort suggested terms, then apply them to a book, series, or your whole account.</li>
        <li>Auto-apply can accept confident AI suggestions in one click while leaving uncertain items for review.</li>
      </ul>      
      
      <p><strong>Support & FAQs</strong></p>
      <ul>
        <li>Support Articles now use a roomier in-app layout with dedicated Concepts and FAQs sections.</li>
        <li>New and expanded help topics cover AI Glossary Scan, replacing manuscripts, pausing or resuming subscriptions, and troubleshooting guidance.</li>
        <li>Support FAQ answers are now grouped into Translation and Content, Account and Billing, and Referrals and Promotions for faster browsing.</li>
        <li>Account FAQ now stays aligned with Support Articles so answers remain consistent across both screens.</li>
        <li>Account FAQ adds keyword search with result counts, so it’s easier to find the right answer quickly.</li>
      </ul>

      <p><strong>Account & Library</strong></p>
      <ul>
        <li>The Account “Take a Tour” action now lives in the page header for quicker access.</li>
        <li>Account and Manage Books header cards were refreshed to match the Support/About look and feel.</li>
        <li>Manage Books now keeps filter and search controls in the header card to speed up access.</li>
      </ul>

      <p><strong>About Page</strong></p>
      <ul>
        <li>About’s top section was simplified by removing extra CTA buttons to reduce visual clutter.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
{/* January 2026 */}
<Panel header={monthHeader('January 2026', 4)} key="january2026" style={panelStyle}>
  <Collapse bordered={false} expandIconPosition="end">
    <Panel header="January 23, 2026" key="2026-01-23" style={panelStyle}>
      <p><strong>Translation Quality</strong></p>
      <ul>
        <li>Added a "Clean Romance Readers" audience option to keep translations aligned with closed-door content expectations.</li>
        <li>Added guards to prevent an issue where German dialogue could occasionally start with two opening guillemets.</li>
        <li>Updated Hindi rules to use native digit formatting and more natural name handling for smoother, clearer translations.</li>
        <li>Translation pop-ups now remember the language you have selected in the book view, so you don’t have to rechoose it when opening a modal.</li>
      </ul>
    </Panel>
    <Panel header="January 8, 2026" key="2026-01-08" style={panelStyle}>
      <p><strong>Chapter Editing (Beta)</strong></p>
      <ul>
        <li>Manual chapter editing is now in beta, letting you edit original, translated, and proofread chapter text directly in the editor.</li>
        <li>Editing is blocked while translation or proofreading jobs are running for that chapter.</li>
        <li>Original text cannot be edited once an original proofread exists; proofread text can only be edited after a proofread version exists.</li>
        <li>Editing the original marks related translations/proofreads as stale, and editing a translation marks its proofread as stale.</li>
        <li>Chapter editing launched through the beta program before becoming generally available in the June 9, 2026 update.</li>
      </ul>

      <p><strong>Default Book Tab</strong></p>
      <ul>
        <li>You can now choose the default tab when opening a book in Account Settings.</li>
        <li>Options include Dashboard (new default) or Metadata (previous behavior).</li>
      </ul>
    </Panel>
    <Panel header="January 7, 2026" key="2026-01-07" style={panelStyle}>
      <p><strong>Exports</strong></p>
      <ul>
        <li>Exports now download immediately when safe, and only queue in Processing Status when they need background processing.</li>
        <li>Completed exports show up in recent export lists right away, even for direct downloads.</li>
        <br/>You may need to do a hard refresh to see these changes:
        <li>Windows: press <strong>Ctrl</strong> + <strong>F5</strong> (or <strong>Ctrl</strong> + <strong>Shift</strong> + <strong>R</strong>) to force a full refresh.</li>
        <li>Mac: press <strong>Cmd</strong> + <strong>Shift</strong> + <strong>R</strong> to force a full refresh.</li>
      </ul>
    </Panel>
    <Panel header="January 6, 2026" key="2026-01-06" style={panelStyle}>
      <p><strong>Book Dashboard</strong></p>
      <ul>
        <li>New Book Dashboard highlights translation progress, recommends next steps, and provides quick links for metadata, translation, proofreading, audio, uploads, and exports.</li>
        <li>Steps automatically mark as complete when they are done in the system, or you can mark items done if you already finished them or want them hidden.</li>
      </ul>

      <p><strong>Export History & Downloads</strong></p>
      <ul>
        <li>Exports now run in the background, and you can track progress in the Processing Status panel.</li>
        <li>The new Export list in the Book Dashboard keeps recent exports visible, and finished downloads appear there for your selected language.</li>
        <li>Downloads expire after 14 days, and older exports per language are cleaned up automatically to keep the list tidy.</li>
      </ul>

      <p><strong>Metadata Translation Rules</strong></p>
      <ul>
        <li>Your Always Translate and Never Translate term lists now apply when translating titles, series names, blurbs, and audiobook credits.</li>
      </ul>
    </Panel>
  </Collapse>
</Panel>
  </>
)

export default getUpdates2026Panels
